import Konva from 'konva';
import React, { useMemo } from 'react';
import { Text, Group, Rect } from 'react-konva';

import { UserRewindPersonFragmentFragment } from 'types/graphql-api.generated';

import FlashbackLayout from 'common/components/RewindModal/Konvas/FlashbackLayout';
import KonvaImage from 'common/components/RewindModal/Konvas/KonvaImage';
import ShareableStage from 'common/components/RewindModal/Konvas/ShareableStage';
import {
  LAYER_WIDTH,
  LAYER_HEIGHT,
  BLOCK_MARGIN,
  FLASHBACK_COVER_LOGO_WIDTH,
  FLASHBACK_COVER_LOGO_HEIGHT,
  FLASHBACK_COVER_LOGO_TOP_MARGIN,
  FLASHBACK_COVER_LOGO_OPACITY,
  ALLOCINE_LOGO_WIDTH,
  ALLOCINE_LOGO_HEIGHT,
  ALLOCINE_LOGO_BOTTOM_MARGIN,
  FLASHBACK_BACKGROUND_COLOR,
  FLASHBACK_TEXT_COLOR,
  FLASHBACK_CELEBRITY_POLAROID_WIDTH,
  FLASHBACK_CELEBRITY_POLAROID_HEIGHT,
  FLASHBACK_CELEBRITY_POLAROID_ROTATION,
  FLASHBACK_CELEBRITY_POLAROID_PADDING,
  FLASHBACK_CELEBRITY_POLAROID_CORNER_RADIUS,
  FLASHBACK_CELEBRITY_POLAROID_BACKGROUND_COLOR,
  FLASHBACK_CELEBRITY_IMAGE_WIDTH,
  FLASHBACK_CELEBRITY_IMAGE_HEIGHT,
  FLASHBACK_CELEBRITY_IMAGE_CORNER_RADIUS,
  FLASHBACK_CELEBRITY_BADGE_WIDTH,
  FLASHBACK_CELEBRITY_BADGE_HEIGHT,
  FLASHBACK_CELEBRITY_BADGE_ROTATION,
  FLASHBACK_CELEBRITY_BADGE_OFFSET_X,
  FLASHBACK_CELEBRITY_BADGE_OFFSET_Y,
  FLASHBACK_CELEBRITY_BADGE_CORNER_RADIUS,
  FLASHBACK_CELEBRITY_NAME_FONT_SIZE,
  FLASHBACK_CELEBRITY_NAME_FONT_STYLE,
  FLASHBACK_CELEBRITY_NAME_LINE_HEIGHT,
  FLASHBACK_CELEBRITY_NAME_COLOR,
  FLASHBACK_CELEBRITY_NAME_BACKGROUND_COLOR,
  FLASHBACK_CELEBRITY_NAME_PADDING,
  FLASHBACK_CELEBRITY_NAME_MARGIN_TOP,
  FLASHBACK_CELEBRITY_NAME_SPACING,
  FLASHBACK_CELEBRITY_LOGO_TO_TITLE_SPACING,
  FLASHBACK_CELEBRITY_TITLE_FONT_SIZE,
  FLASHBACK_CELEBRITY_TITLE_FONT_STYLE,
  FLASHBACK_CELEBRITY_TITLE_LINE_HEIGHT,
  FLASHBACK_CELEBRITY_SUBTITLE_FONT_SIZE,
  FLASHBACK_CELEBRITY_SUBTITLE_FONT_STYLE,
  FLASHBACK_CELEBRITY_SUBTITLE_LINE_HEIGHT,
  FLASHBACK_CELEBRITY_TITLE_TO_SUBTITLE_SPACING,
  FLASHBACK_CELEBRITY_SUBTITLE_TO_POLAROID_SPACING
} from 'common/components/RewindModal/Konvas/constants';
import { IMG_BACKGROUND_STAR_SHARE } from 'common/configuration/constants';
import { IMAGE_EMPTY_PHOTO_PORTRAIT } from 'common/configuration/constants';

const CELEBRITY_FONT_FAMILY = 'Montserrat, arial, sans-serif';

type ScreenFavoriteCelebrityProps = {
  celebrity?: UserRewindPersonFragmentFragment;
  imageName?: string;
  title?: string;
  subtitle?: string;
};

const ShareableScreenFavoriteCelebrity = React.forwardRef<
  any,
  ScreenFavoriteCelebrityProps
>(({ celebrity, imageName, title, subtitle }, ref) => {
  const badgeUrl = imageName
    ? `/commons/v9/allocine/flashback/${imageName}.png`
    : null;

  const celebrityImagePath =
    celebrity?.picture?.path || IMAGE_EMPTY_PHOTO_PORTRAIT;

  const layout = useMemo(() => {
    const topLogoBottomY =
      FLASHBACK_COVER_LOGO_TOP_MARGIN + FLASHBACK_COVER_LOGO_HEIGHT;
    const bottomLogoTopY =
      LAYER_HEIGHT - ALLOCINE_LOGO_HEIGHT - ALLOCINE_LOGO_BOTTOM_MARGIN;

    const titleHeight =
      FLASHBACK_CELEBRITY_TITLE_FONT_SIZE *
      FLASHBACK_CELEBRITY_TITLE_LINE_HEIGHT;
    const subtitleHeight =
      FLASHBACK_CELEBRITY_SUBTITLE_FONT_SIZE *
      FLASHBACK_CELEBRITY_SUBTITLE_LINE_HEIGHT;

    const titleY = topLogoBottomY + FLASHBACK_CELEBRITY_LOGO_TO_TITLE_SPACING;
    const subtitleY =
      titleY + titleHeight + FLASHBACK_CELEBRITY_TITLE_TO_SUBTITLE_SPACING;
    const polaroidStartY =
      subtitleY +
      subtitleHeight +
      FLASHBACK_CELEBRITY_SUBTITLE_TO_POLAROID_SPACING;

    const availableHeight = bottomLogoTopY - polaroidStartY;
    const polaroidY = polaroidStartY + availableHeight / 2;
    const centerX = LAYER_WIDTH / 2;
    const imageX =
      (FLASHBACK_CELEBRITY_POLAROID_WIDTH - FLASHBACK_CELEBRITY_IMAGE_WIDTH) /
      2;

    return {
      polaroidX: centerX,
      polaroidY,
      imageX,
      imageY: FLASHBACK_CELEBRITY_POLAROID_PADDING,
      badgeX:
        centerX +
        FLASHBACK_CELEBRITY_POLAROID_WIDTH +
        FLASHBACK_CELEBRITY_BADGE_OFFSET_X,
      badgeY: polaroidY + FLASHBACK_CELEBRITY_BADGE_OFFSET_Y,
      titleY,
      subtitleY
    };
  }, []);

  const firstNameText = celebrity?.firstName || '';
  const lastNameText = celebrity?.lastName || '';

  // Calculate text widths using Konva Text measurement
  const nameWidths = useMemo(() => {
    const tempText = new Konva.Text({
      fontSize: FLASHBACK_CELEBRITY_NAME_FONT_SIZE,
      fontFamily: CELEBRITY_FONT_FAMILY,
      fontStyle: FLASHBACK_CELEBRITY_NAME_FONT_STYLE
    });

    const firstNameWidth = firstNameText
      ? (() => {
          tempText.text(firstNameText);
          return tempText.getClientRect().width;
        })()
      : 0;
    const lastNameWidth = lastNameText
      ? (() => {
          tempText.text(lastNameText);
          return tempText.getClientRect().width;
        })()
      : 0;

    return {
      firstName: firstNameWidth + FLASHBACK_CELEBRITY_NAME_PADDING * 2,
      lastName: lastNameWidth + FLASHBACK_CELEBRITY_NAME_PADDING * 2
    };
  }, [firstNameText, lastNameText]);

  return (
    <ShareableStage ref={ref}>
      <FlashbackLayout
        backgroundColor={FLASHBACK_BACKGROUND_COLOR}
        backgroundOpacity={1}
        backgroundImage={IMG_BACKGROUND_STAR_SHARE}
        coverLogoWidth={FLASHBACK_COVER_LOGO_WIDTH}
        coverLogoHeight={FLASHBACK_COVER_LOGO_HEIGHT}
        coverLogoTopMargin={FLASHBACK_COVER_LOGO_TOP_MARGIN}
        coverLogoOpacity={FLASHBACK_COVER_LOGO_OPACITY}
        allocineLogoWidth={ALLOCINE_LOGO_WIDTH}
        allocineLogoHeight={ALLOCINE_LOGO_HEIGHT}
        allocineLogoBottomMargin={ALLOCINE_LOGO_BOTTOM_MARGIN}
      >
        {title && (
          <Text
            text={title}
            fontSize={FLASHBACK_CELEBRITY_TITLE_FONT_SIZE}
            fontFamily={CELEBRITY_FONT_FAMILY}
            fontStyle={FLASHBACK_CELEBRITY_TITLE_FONT_STYLE}
            lineHeight={FLASHBACK_CELEBRITY_TITLE_LINE_HEIGHT}
            fill={FLASHBACK_TEXT_COLOR}
            width={LAYER_WIDTH - BLOCK_MARGIN * 4}
            x={BLOCK_MARGIN * 2}
            align="center"
            y={layout.titleY}
          />
        )}

        {subtitle && (
          <Text
            text={subtitle}
            fontSize={FLASHBACK_CELEBRITY_SUBTITLE_FONT_SIZE}
            fontFamily={CELEBRITY_FONT_FAMILY}
            fontStyle={FLASHBACK_CELEBRITY_SUBTITLE_FONT_STYLE}
            lineHeight={FLASHBACK_CELEBRITY_SUBTITLE_LINE_HEIGHT}
            fill={FLASHBACK_TEXT_COLOR}
            width={LAYER_WIDTH - BLOCK_MARGIN * 4}
            x={BLOCK_MARGIN * 2}
            align="center"
            y={layout.subtitleY}
          />
        )}

        <Group
          x={layout.polaroidX}
          y={layout.polaroidY}
          rotation={FLASHBACK_CELEBRITY_POLAROID_ROTATION}
          offsetX={FLASHBACK_CELEBRITY_POLAROID_WIDTH / 2}
          offsetY={FLASHBACK_CELEBRITY_POLAROID_HEIGHT / 2}
        >
          <Rect
            width={FLASHBACK_CELEBRITY_POLAROID_WIDTH}
            height={FLASHBACK_CELEBRITY_POLAROID_HEIGHT}
            fill={FLASHBACK_CELEBRITY_POLAROID_BACKGROUND_COLOR}
            cornerRadius={FLASHBACK_CELEBRITY_POLAROID_CORNER_RADIUS}
          />

          <KonvaImage
            path={celebrityImagePath}
            x={layout.imageX}
            y={layout.imageY}
            width={FLASHBACK_CELEBRITY_IMAGE_WIDTH}
            height={FLASHBACK_CELEBRITY_IMAGE_HEIGHT}
            cornerRadius={FLASHBACK_CELEBRITY_IMAGE_CORNER_RADIUS}
          />

          {[firstNameText, lastNameText].map((text, index) => {
            if (!text) return null;

            const nameHeight =
              FLASHBACK_CELEBRITY_NAME_FONT_SIZE *
                FLASHBACK_CELEBRITY_NAME_LINE_HEIGHT +
              FLASHBACK_CELEBRITY_NAME_PADDING * 2;
            const nameY =
              FLASHBACK_CELEBRITY_IMAGE_HEIGHT +
              FLASHBACK_CELEBRITY_NAME_MARGIN_TOP +
              (index === 1 ? nameHeight + FLASHBACK_CELEBRITY_NAME_SPACING : 0);

            const nameWidth =
              index === 0 ? nameWidths.firstName : nameWidths.lastName;

            return (
              <Group key={index} y={nameY}>
                <Rect
                  width={nameWidth}
                  height={nameHeight}
                  fill={FLASHBACK_CELEBRITY_NAME_BACKGROUND_COLOR}
                  x={FLASHBACK_CELEBRITY_POLAROID_PADDING}
                  y={0}
                />
                <Text
                  text={text}
                  fontSize={FLASHBACK_CELEBRITY_NAME_FONT_SIZE}
                  fontFamily={CELEBRITY_FONT_FAMILY}
                  fontStyle={FLASHBACK_CELEBRITY_NAME_FONT_STYLE}
                  lineHeight={FLASHBACK_CELEBRITY_NAME_LINE_HEIGHT}
                  fill={FLASHBACK_CELEBRITY_NAME_COLOR}
                  x={
                    FLASHBACK_CELEBRITY_POLAROID_PADDING +
                    FLASHBACK_CELEBRITY_NAME_PADDING
                  }
                  y={FLASHBACK_CELEBRITY_NAME_PADDING}
                />
              </Group>
            );
          })}
        </Group>

        {badgeUrl && (
          <Group
            x={layout.badgeX}
            y={layout.badgeY}
            rotation={FLASHBACK_CELEBRITY_BADGE_ROTATION}
            offsetX={FLASHBACK_CELEBRITY_BADGE_WIDTH / 2}
            offsetY={FLASHBACK_CELEBRITY_BADGE_HEIGHT / 2}
          >
            <KonvaImage
              path={badgeUrl}
              isLocal={false}
              x={0}
              y={0}
              width={FLASHBACK_CELEBRITY_BADGE_WIDTH}
              height={FLASHBACK_CELEBRITY_BADGE_HEIGHT}
              cornerRadius={FLASHBACK_CELEBRITY_BADGE_CORNER_RADIUS}
            />
          </Group>
        )}
      </FlashbackLayout>
    </ShareableStage>
  );
});

ShareableScreenFavoriteCelebrity.displayName =
  'ShareableScreenFavoriteCelebrity';
export default ShareableScreenFavoriteCelebrity;
